import type { CommittedJob, EngineSettings, ISODate, Machine, MachiningStep } from "./types.js";

/** Hours already used on each machine, per working day: machine id → date → hours. */
export type CapacityLedger = Map<string, Map<ISODate, number>>;

/** Hours each machine can run on one working day (decision D49). */
export function dailyCapacityByMachine(settings: EngineSettings): Map<string, number> {
  const days = settings.workingDays.length;
  return new Map(
    settings.machines.map((m) => {
      if (!(m.weeklyHours >= 0) || !Number.isFinite(m.weeklyHours)) throw new Error(`Machine ${m.id}: weeklyHours must be a finite number >= 0`);
      return [m.id, m.weeklyHours / days];
    }),
  );
}

/** The shop's total hours in a week, across every machine. */
export function shopWeeklyHours(machines: readonly Machine[]): number {
  return machines.reduce((sum, m) => sum + m.weeklyHours, 0);
}

/** A machining step's total hours, across its machines. */
export function stepHours(step: MachiningStep): number {
  return step.machines.reduce((sum, m) => sum + m.hours, 0);
}

/** Hours already committed per machine per working day, from other jobs' machining windows (D12, D49). */
export function buildCommittedLedger(committedJobs: readonly CommittedJob[]): CapacityLedger {
  const ledger: CapacityLedger = new Map();
  for (const job of committedJobs) {
    for (const window of job.machining) {
      for (const a of window.allocations) addToLedger(ledger, a.machineId, a.date, a.hours);
    }
  }
  return ledger;
}

export function addToLedger(ledger: CapacityLedger, machineId: string, date: ISODate, hours: number): void {
  if (hours <= 0) return;
  let days = ledger.get(machineId);
  if (!days) ledger.set(machineId, (days = new Map()));
  days.set(date, (days.get(date) ?? 0) + hours);
}

export function availableHoursOn(machineId: string, date: ISODate, dailyCapacity: ReadonlyMap<string, number>, ledger: CapacityLedger): number {
  return Math.max(0, (dailyCapacity.get(machineId) ?? 0) - (ledger.get(machineId)?.get(date) ?? 0));
}
